import {useEffect, useRef} from 'react';

import {StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {getStaticTaskNodes, TaskSchemaStaticNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChange, FlowchartNodeChangeType} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {useFlowchartSidepanelManagerContext} from '../right-sidepanel/FlowchartSidepanelUnsavedChangesContext';
import {ApplyFlowchartChangesFn} from './FlowchartStateTypes';

interface UseSidepanelDeselectionParams {
  applyFlowchartChanges: ApplyFlowchartChangesFn;
}

interface SidepanelState {
  nodeId: string | null;
  fieldId: string | null;
}

type SectionRow = TaskSchemaStaticNodeData['sections'][number]['sectionRows'][number];

/**
 * Finds a row by its clientId (fieldId) within a node.
 */
function findRowByFieldId(
  staticFlowchart: StaticTemplateFlowchart,
  nodeId: string,
  fieldId: string
): SectionRow | undefined {
  const [node] = getStaticTaskNodes(staticFlowchart.nodesStaticData, (node) => node.id === nodeId);

  if (node == null) {
    return undefined;
  }

  for (const section of node.sections) {
    const row = section.sectionRows.find((row) => row.clientId === fieldId);
    if (row != null) {
      return row;
    }
  }

  return undefined;
}

/**
 * Deselects a field (row) if it exists in the flowchart.
 */
function deselectFieldIfExists(
  staticFlowchart: StaticTemplateFlowchart,
  nodeId: string,
  fieldId: string,
  nodeChanges: Array<FlowchartNodeChange>
): void {
  const row = findRowByFieldId(staticFlowchart, nodeId, fieldId);
  if (row != null) {
    nodeChanges.push({
      type: FlowchartNodeChangeType.SELECT_ROW,
      id: nodeId,
      rowId: row.id,
      selected: false,
    });
  }
}

/**
 * Deselects a node and its field (if any).
 */
function deselectNodeAndField(
  staticFlowchart: StaticTemplateFlowchart,
  nodeId: string,
  fieldId: string | null,
  nodeChanges: Array<FlowchartNodeChange>
): void {
  nodeChanges.push({
    type: FlowchartNodeChangeType.SELECT,
    id: nodeId,
    selected: false,
  });
  if (fieldId != null) {
    deselectFieldIfExists(staticFlowchart, nodeId, fieldId, nodeChanges);
  }
}

/**
 * Handles deselection when the sidepanel closes (node becomes null).
 */
function handleSidepanelClose(
  staticFlowchart: StaticTemplateFlowchart,
  previousState: SidepanelState,
  nodeChanges: Array<FlowchartNodeChange>
): void {
  if (previousState.nodeId != null) {
    deselectNodeAndField(staticFlowchart, previousState.nodeId, previousState.fieldId, nodeChanges);
  }
}

/**
 * Handles deselection when the node changes to a different node.
 */
function handleNodeChange(
  staticFlowchart: StaticTemplateFlowchart,
  previousState: SidepanelState,
  nodeChanges: Array<FlowchartNodeChange>
): void {
  if (previousState.nodeId != null) {
    deselectNodeAndField(staticFlowchart, previousState.nodeId, previousState.fieldId, nodeChanges);
  }
}

/**
 * Handles deselection when the field changes in the same node.
 */
function handleFieldChange(
  staticFlowchart: StaticTemplateFlowchart,
  previousState: SidepanelState,
  nodeChanges: Array<FlowchartNodeChange>
): void {
  if (previousState.nodeId != null && previousState.fieldId != null) {
    deselectFieldIfExists(staticFlowchart, previousState.nodeId, previousState.fieldId, nodeChanges);
  }
}

/**
 * Hook that handles deselecting nodes and rows when the sidepanel state changes.
 *
 * Handles all deselection scenarios:
 * - Sidepanel closes (node becomes null)
 * - Node changes to a different node
 * - Field changes to a different field in the same node
 * - Field becomes null in the same node
 */
export function useSidepanelDeselection({applyFlowchartChanges}: UseSidepanelDeselectionParams): void {
  const sidepanelManager = useFlowchartSidepanelManagerContext();

  const previousSidepanelStateRef = useRef<SidepanelState>({
    nodeId: null,
    fieldId: null,
  });

  useEffect(() => {
    const effectiveState = sidepanelManager.getEffectiveState();
    const previousState = previousSidepanelStateRef.current;
    const currentNodeId = effectiveState.node?.id ?? null;
    const currentFieldId = effectiveState.fieldId;

    if (previousState.nodeId === currentNodeId && previousState.fieldId === currentFieldId) {
      return;
    }

    applyFlowchartChanges((flowchartConfig: FlowchartConfigWithMetadata) => {
      const staticFlowchart = flowchartConfig.staticFlowchart;
      const nodeChanges: Array<FlowchartNodeChange> = [];

      if (previousState.nodeId != null && currentNodeId == null) {
        handleSidepanelClose(staticFlowchart, previousState, nodeChanges);
      } else if (
        previousState.nodeId != null &&
        currentNodeId != null &&
        previousState.nodeId !== currentNodeId
      ) {
        handleNodeChange(staticFlowchart, previousState, nodeChanges);
      } else if (
        previousState.nodeId != null &&
        currentNodeId === previousState.nodeId &&
        previousState.fieldId != null &&
        previousState.fieldId !== currentFieldId
      ) {
        handleFieldChange(staticFlowchart, previousState, nodeChanges);
      }

      return {nodeChanges};
    });

    previousSidepanelStateRef.current = {
      nodeId: currentNodeId,
      fieldId: currentFieldId,
    };
  }, [sidepanelManager, applyFlowchartChanges]);
}
